import { useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import {
  AtsAssignVacancyToJobApplicationDocument,
  AtsAssignVacancyToJobApplicationMutation,
  AtsJobApplicationAddToVacancyFragment,
  AtsJobApplicationHeaderDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { VacancySelector } from 'modules/ats/components/VacancySelector'

interface Props {
  application: AtsJobApplicationAddToVacancyFragment
  onClose: () => void
}

export const AddToVacancyModal = ({ application, onClose }: Props) => {
  const [selectedVacancyId, setSelectedVacancyId] = useState<string | undefined>(undefined)

  const [assignVacancy, { client }] = useMutation(AtsAssignVacancyToJobApplicationDocument, {
    extractorFn: (result: AtsAssignVacancyToJobApplicationMutation) =>
      result.atsMutations.assignApplicationAtsVacancy,
  })

  const handleAssign = async () => {
    if (!selectedVacancyId) return

    const result = await assignVacancy({
      variables: {
        applicationId: application.id,
        id: selectedVacancyId,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.application_details.header.add_to_vacancy.error_message'),
      })
    })

    if (handled.success) {
      void client.refetchQueries({ include: [AtsJobApplicationHeaderDocument] })
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.application_details.header.add_to_vacancy.success_message'),
      })
    }

    onClose()
  }

  return (
    <F0Dialog
      title={i18n.t('ats.application_details.header.add_to_vacancy.title')}
      isOpen
      onClose={onClose}
      primaryAction={{
        label: i18n.t('ats.application_details.header.add_to_vacancy.assign_button'),
        onClick: handleAssign,
        disabled: !selectedVacancyId,
      }}
      secondaryAction={{
        label: i18n.t('ats.application_details.header.add_to_vacancy.cancel_button'),
        onClick: onClose,
      }}
    >
      <VacancySelector
        jobPostingId={application.jobPosting.id}
        value={selectedVacancyId}
        onChange={setSelectedVacancyId}
      />
    </F0Dialog>
  )
}
